import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import {
  BRICK_PROFILE,
  CONCRETE_PROFILE,
  IMPACT_KINDS,
  PARTICLE_PROFILES,
  STONE_PROFILE,
  createParticleWorld,
  getProfile,
  type HeightSample,
  type ImpactKind,
  type LastBurstCall,
  type ParticleMaterialProfile,
  type ParticleTier,
  type ParticleWorld,
} from '../effects/particles';
import {
  ENERGY_DEFAULT,
  energyToSlider,
  sliderToEnergy,
  snapEnergy,
} from '../services/energy';
import { LAB_ARENA, LAB_COLLIDERS } from '../effects/particles/arena';
import { useViewportWidth } from '../hooks/useViewportWidth';
import '../components/MobileLabChrome.css';
import './ParticleLab.css';

const MOBILE_BREAKPOINT = 768;

type LabProfileId = 'brick' | 'concrete' | 'stone';

interface ParticleLabState {
  ready: boolean;
  liveGrains: number;
  liveFines: number;
  liveChunks: number;
  airborneChunks: number;
  settledChunks: number;
  bleedEmissions: number;
  jointPuffEmissions: number;
  simTime: number;
  pileVolume: number;
  bursts: number;
  fps: number;
  frame: number;
  budgetPressure: number;
  sixWay: boolean;
  lightOrbit: boolean;
  tier: ParticleTier;
  fineCapacity: number;
  weapon: ImpactKind;
}

interface ParticleLabProbe {
  burstAt: (
    x: number,
    y: number,
    z: number,
    dx: number,
    dy: number,
    dz: number,
    mass?: number,
    nx?: number,
    ny?: number,
    nz?: number,
  ) => void;
  readFinesMeanVelocity?: () => { x: number; y: number; z: number; count: number } | null;
  readFinesOpacity?: () => { meanAlpha: number; meanLife: number; count: number } | null;
  readFinesLive?: () => Array<{ x: number; y: number; z: number; vx: number; vy: number; vz: number }> | null;
  setMaterial: (id: LabProfileId) => void;
  setEnergy: (e: number) => void;
  setWeapon: (kind: ImpactKind) => void;
  reset: () => void;
  setCamera: (px: number, py: number, pz: number, tx: number, ty: number, tz: number) => void;
  getCamera?: () => {
    x: number;
    y: number;
    z: number;
    tx: number;
    ty: number;
    tz: number;
    azimuth: number;
    polar: number;
  } | null;
  setHeightfieldView: (on: boolean) => void;
  setDust: (on: boolean) => void;
  setGrainsVisible?: (on: boolean) => void;
  setSixWay: (on: boolean) => void;
  setLightOrbit: (on: boolean) => void;
  setLightOrbitRate: (radPerSec: number) => void;
  setLightDir: (x: number, y: number, z: number) => void;
  setTier: (tier: ParticleTier) => void;
  burstMany: (
    items: Array<{
      x: number;
      y: number;
      z: number;
      dx: number;
      dy: number;
      dz: number;
      mass?: number;
      energy?: number;
      material?: LabProfileId;
      nx?: number;
      ny?: number;
      nz?: number;
    }>,
  ) => void;
  lastBurst: () => LastBurstCall | null;
  sampleHeightfield: (x: number, z: number) => HeightSample | null;
  dumpCloud?: () => Record<string, unknown> | null;
  hideByName?: (name: string, visible: boolean) => void;
}

declare global {
  interface Window {
    __particleLabState?: ParticleLabState;
    __particleLabProbe?: ParticleLabProbe;
  }
}

function writeState(patch: Partial<ParticleLabState>): void {
  const prev = window.__particleLabState;
  window.__particleLabState = {
    ready: patch.ready ?? prev?.ready ?? false,
    liveGrains: patch.liveGrains ?? prev?.liveGrains ?? 0,
    liveFines: patch.liveFines ?? prev?.liveFines ?? 0,
    liveChunks: patch.liveChunks ?? prev?.liveChunks ?? 0,
    airborneChunks: patch.airborneChunks ?? prev?.airborneChunks ?? 0,
    settledChunks: patch.settledChunks ?? prev?.settledChunks ?? 0,
    bleedEmissions: patch.bleedEmissions ?? prev?.bleedEmissions ?? 0,
    jointPuffEmissions: patch.jointPuffEmissions ?? prev?.jointPuffEmissions ?? 0,
    simTime: patch.simTime ?? prev?.simTime ?? 0,
    pileVolume: patch.pileVolume ?? prev?.pileVolume ?? 0,
    bursts: patch.bursts ?? prev?.bursts ?? 0,
    fps: patch.fps ?? prev?.fps ?? 0,
    frame: patch.frame ?? prev?.frame ?? 0,
    budgetPressure: patch.budgetPressure ?? prev?.budgetPressure ?? 0,
    sixWay: patch.sixWay ?? prev?.sixWay ?? true,
    lightOrbit: patch.lightOrbit ?? prev?.lightOrbit ?? false,
    tier: patch.tier ?? prev?.tier ?? 'desktop',
    fineCapacity: patch.fineCapacity ?? prev?.fineCapacity ?? 0,
    weapon: patch.weapon ?? prev?.weapon ?? 'cannon',
  };
}

const DEFAULT_LIGHT: [number, number, number] = [6, 10, 4];
const LIGHT_ORBIT_RATE = 0.15;

const PROFILE_BY_ID: Record<LabProfileId, ParticleMaterialProfile> = {
  brick: BRICK_PROFILE,
  concrete: CONCRETE_PROFILE,
  stone: STONE_PROFILE,
};

function detectTier(): 'desktop' | 'mobile' {
  if (typeof window === 'undefined') return 'desktop';
  return window.innerWidth < 720 ? 'mobile' : 'desktop';
}

function LabScene({
  worldRef,
  profileRef,
  energyRef,
  weaponRef,
  controlsRef,
  hfCanvasRef,
  showHfRef,
  sixWayRef,
  lightOrbitRef,
  lightRateRef,
  lightPosRef,
  sceneDumpRef,
  worldTier,
  onHud,
  onBurst,
}: {
  worldRef: MutableRefObject<ParticleWorld | null>;
  profileRef: MutableRefObject<ParticleMaterialProfile>;
  energyRef: MutableRefObject<number>;
  weaponRef: MutableRefObject<ImpactKind>;
  controlsRef: MutableRefObject<OrbitControlsImpl | null>;
  hfCanvasRef: MutableRefObject<HTMLCanvasElement | null>;
  showHfRef: MutableRefObject<boolean>;
  sixWayRef: MutableRefObject<boolean>;
  lightOrbitRef: MutableRefObject<boolean>;
  lightRateRef: MutableRefObject<number>;
  lightPosRef: MutableRefObject<[number, number, number]>;
  sceneDumpRef: MutableRefObject<THREE.Scene | null>;
  worldTier: ParticleTier;
  onHud: (s: {
    fps: number;
    live: number;
    fines: number;
    vol: number;
    bursts: number;
    frame: number;
    ready: boolean;
    budgetPressure: number;
    sixWay: boolean;
    fineCapacity: number;
    lastBurst: LastBurstCall | null;
  }) => void;
  onBurst?: () => void;
}) {
  const { gl, scene, camera } = useThree();
  sceneDumpRef.current = scene;
  const raycaster = useRef(new THREE.Raycaster());
  const ndc = useRef(new THREE.Vector2());
  const fpsAcc = useRef({ frames: 0, last: performance.now(), fps: 0 });
  const lastBridge = useRef(0);
  const lightRef = useRef<THREE.DirectionalLight>(null);
  const orbitAngle = useRef(Math.atan2(DEFAULT_LIGHT[2], DEFAULT_LIGHT[0]));

  useLayoutEffect(() => {
    const world = createParticleWorld(gl, {
      arenaBounds: LAB_ARENA,
      tier: worldTier,
      colliders: LAB_COLLIDERS,
    });
    world.setSixWay(sixWayRef.current);
    world.setLightDir(DEFAULT_LIGHT[0], DEFAULT_LIGHT[1], DEFAULT_LIGHT[2]);
    world.attach(scene);
    worldRef.current = world;
    writeState({
      ready: world.ready,
      liveGrains: 0,
      liveFines: 0,
      pileVolume: 0,
      bursts: 0,
      fps: 0,
      frame: 0,
      budgetPressure: 0,
      sixWay: sixWayRef.current,
      tier: worldTier,
      fineCapacity: world.getStats().fineCapacity,
    });
    return () => {
      world.detach();
      world.dispose();
      worldRef.current = null;
    };
  }, [gl, scene, worldRef, worldTier, sixWayRef]);

  const fireAtClient = useCallback(
    (clientX: number, clientY: number) => {
      const world = worldRef.current;
      if (!world?.ready) return;
      const el = gl.domElement;
      const rect = el.getBoundingClientRect();
      ndc.current.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.current.setFromCamera(ndc.current, camera);
      const hits = raycaster.current.intersectObjects(world.getPickables(), false);
      if (hits.length === 0) return;
      const hit = hits[0];
      const worldN = new THREE.Vector3(0, 1, 0);
      if (hit.normal && hit.normal.lengthSq() > 1e-8) {
        worldN.copy(hit.normal).normalize();
      } else if (hit.face?.normal) {
        worldN.copy(hit.face.normal).transformDirection(hit.object.matrixWorld).normalize();
      }
      const origin = hit.point.clone().add(worldN.clone().multiplyScalar(0.08));
      const dir = origin.clone().sub(camera.position);
      dir.y += 0.28;
      dir.normalize();
      world.burst({
        position: [origin.x, origin.y, origin.z],
        direction: [dir.x, dir.y, dir.z],
        energy: energyRef.current,
        mass: 1.25,
        profile: profileRef.current,
        surfaceNormal: [worldN.x, worldN.y, worldN.z],
        impact: { kind: weaponRef.current },
      });
      onBurst?.();
    },
    [camera, gl, energyRef, profileRef, weaponRef, worldRef, onBurst],
  );

  useEffect(() => {
    const canvas = gl.domElement;
    let down: { x: number; y: number; t: number } | null = null;

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      down = { x: e.clientX, y: e.clientY, t: performance.now() };
    };
    const onUp = (e: PointerEvent) => {
      if (!down || e.button !== 0) return;
      const dist = Math.hypot(e.clientX - down.x, e.clientY - down.y);
      const dt = performance.now() - down.t;
      down = null;
      if (dist <= 5 && dt <= 400) fireAtClient(e.clientX, e.clientY);
    };
    const flushOrbitPointer = (event: PointerEvent) => {
      // three-stdlib OrbitControls never listens for pointercancel. A cancelled
      // finger stays in its pointers[] and 3+ ghosts force STATE.NONE — orbit
      // dies. Synthesize the pointerup it already handles.
      document.dispatchEvent(
        new PointerEvent('pointerup', {
          bubbles: true,
          cancelable: true,
          pointerId: event.pointerId,
          pointerType: event.pointerType,
          isPrimary: event.isPrimary,
          clientX: event.clientX,
          clientY: event.clientY,
          button: 0,
          buttons: 0,
        }),
      );
    };
    const onCancel = (event: PointerEvent) => {
      down = null;
      flushOrbitPointer(event);
    };
    const onContext = (e: MouseEvent) => e.preventDefault();
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    document.addEventListener('pointercancel', onCancel);
    canvas.addEventListener('contextmenu', onContext);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
      document.removeEventListener('pointercancel', onCancel);
      canvas.removeEventListener('contextmenu', onContext);
    };
  }, [fireAtClient, gl]);

  useFrame((_, dt) => {
    const world = worldRef.current;
    const orbitR = Math.hypot(DEFAULT_LIGHT[0], DEFAULT_LIGHT[2]);
    if (lightOrbitRef.current) {
      orbitAngle.current += lightRateRef.current * dt;
      lightPosRef.current = [
        Math.cos(orbitAngle.current) * orbitR,
        DEFAULT_LIGHT[1],
        Math.sin(orbitAngle.current) * orbitR,
      ];
    }
    const [lx, ly, lz] = lightPosRef.current;
    if (lightRef.current) lightRef.current.position.set(lx, ly, lz);
    if (world) {
      world.setLightDir(lx, ly, lz);
      world.step(dt, camera);
    }
    const acc = fpsAcc.current;
    acc.frames += 1;
    const now = performance.now();
    if (now - acc.last >= 400) {
      acc.fps = (acc.frames * 1000) / (now - acc.last);
      acc.frames = 0;
      acc.last = now;
    }
    const st = world?.getStats();
    writeState({
      ready: world?.ready ?? false,
      liveGrains: st?.liveGrains ?? 0,
      liveFines: st?.liveFines ?? 0,
      liveChunks: st?.liveChunks ?? 0,
      airborneChunks: st?.airborneChunks ?? 0,
      settledChunks: st?.settledChunks ?? 0,
      bleedEmissions: st?.bleedEmissions ?? 0,
      jointPuffEmissions: st?.jointPuffEmissions ?? 0,
      simTime: st?.simTime ?? 0,
      pileVolume: st?.pileVolume ?? 0,
      bursts: st?.bursts ?? 0,
      fps: acc.fps,
      frame: st?.frame ?? 0,
      budgetPressure: st?.budgetPressure ?? 0,
      sixWay: world?.getSixWay() ?? sixWayRef.current,
      lightOrbit: lightOrbitRef.current,
      tier: worldTier,
      fineCapacity: st?.fineCapacity ?? 0,
    });
    if (now - lastBridge.current >= 100) {
      lastBridge.current = now;
      const payload = {
        fps: acc.fps,
        live: st?.liveGrains ?? 0,
        fines: st?.liveFines ?? 0,
        vol: st?.pileVolume ?? 0,
        bursts: st?.bursts ?? 0,
        frame: st?.frame ?? 0,
        ready: world?.ready ?? false,
        budgetPressure: st?.budgetPressure ?? 0,
        sixWay: world?.getSixWay() ?? sixWayRef.current,
        fineCapacity: st?.fineCapacity ?? 0,
        lastBurst: world?.lastBurst() ?? null,
        liveChunks: st?.liveChunks ?? 0,
        settledChunks: st?.settledChunks ?? 0,
        airborneChunks: st?.airborneChunks ?? 0,
        bleedEmissions: st?.bleedEmissions ?? 0,
        jointPuffEmissions: st?.jointPuffEmissions ?? 0,
        simTime: st?.simTime ?? 0,
      };
      onHud(payload);
      if (showHfRef.current && hfCanvasRef.current && world) {
        world.blitHeightfieldPreview(hfCanvasRef.current);
      }
    }
  });

  return (
    <>
      <color attach="background" args={['#08090c']} />
      <fog attach="fog" args={['#0a0b0e', 24, 54]} />
      <mesh renderOrder={-10}>
        <sphereGeometry args={[42, 24, 16]} />
        <shaderMaterial
          side={THREE.BackSide}
          depthWrite={false}
          fog={false}
          toneMapped={false}
          vertexShader={`
            varying vec3 vDir;
            void main() {
              vDir = position;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `}
          fragmentShader={`
            varying vec3 vDir;
            void main() {
              float h = normalize(vDir).y;
              // Written display-referred so the zenith stays a true low-key void
              // (mobile e2e crop at the top of the frame). Horizon only lifts.
              vec3 top = vec3(0.028, 0.030, 0.036);
              vec3 hzCol = vec3(0.09, 0.082, 0.078);
              vec3 bot = vec3(0.035, 0.032, 0.030);
              vec3 col = mix(bot, hzCol, smoothstep(-0.20, 0.0, h));
              col = mix(col, top, smoothstep(0.04, 0.38, h));
              float hz = 1.0 - smoothstep(0.0, 0.14, abs(h + 0.01));
              col = mix(col, hzCol * 0.65, hz * 0.5);
              gl_FragColor = vec4(col, 1.0);
            }
          `}
        />
      </mesh>
      <hemisphereLight args={['#b8c0cc', '#1a1816', 0.48]} />
      <directionalLight
        ref={lightRef}
        position={[DEFAULT_LIGHT[0], DEFAULT_LIGHT[1], DEFAULT_LIGHT[2]]}
        intensity={1.28}
        color="#fff1d6"
      />
      <OrbitControls
        ref={controlsRef}
        enableDamping={false}
        minPolarAngle={0.12}
        maxPolarAngle={Math.PI / 2 - 0.05}
        minDistance={3}
        maxDistance={22}
        target={[0, 0.35, 0]}
        makeDefault
      />
    </>
  );
}

export default function ParticleLab({ onBack }: { onBack: () => void }) {
  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth < MOBILE_BREAKPOINT;

  const worldRef = useRef<ParticleWorld | null>(null);
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const profileRef = useRef<ParticleMaterialProfile>(BRICK_PROFILE);
  const energyRef = useRef(ENERGY_DEFAULT);
  const weaponRef = useRef<ImpactKind>('cannon');
  const hfCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const showHfRef = useRef(false);
  const sixWayRef = useRef(true);
  const lightOrbitRef = useRef(false);
  const lightRateRef = useRef(LIGHT_ORBIT_RATE);
  const lightPosRef = useRef<[number, number, number]>([...DEFAULT_LIGHT]);
  const sceneDumpRef = useRef<THREE.Scene | null>(null);

  const [material, setMaterial] = useState<LabProfileId>('brick');
  const [weapon, setWeaponState] = useState<ImpactKind>('cannon');
  const [energy, setEnergy] = useState(ENERGY_DEFAULT);
  const [showHf, setShowHf] = useState(false);
  const [dustOn, setDustOn] = useState(true);
  const [sixWay, setSixWayOn] = useState(true);
  const [lightOrbit, setLightOrbitOn] = useState(false);
  const [worldTier, setWorldTier] = useState<ParticleTier>(() => detectTier());
  const [apiOpen, setApiOpen] = useState(false);
  const [hud, setHud] = useState({
    fps: 0,
    live: 0,
    fines: 0,
    vol: 0,
    bursts: 0,
    frame: 0,
    ready: false,
    budgetPressure: 0,
    sixWay: true,
    fineCapacity: 0,
    lastBurst: null as LastBurstCall | null,
  });
  const [statsOpen, setStatsOpen] = useState(false);
  const [hintVisible, setHintVisible] = useState(true);

  profileRef.current = PROFILE_BY_ID[material];
  energyRef.current = energy;
  weaponRef.current = weapon;
  showHfRef.current = showHf;
  sixWayRef.current = sixWay;
  lightOrbitRef.current = lightOrbit;

  const dismissHint = useCallback(() => setHintVisible(false), []);

  // First-visit mobile toast: auto-fade after ~4s (or earlier on burst).
  useEffect(() => {
    if (!isMobile || !hintVisible) return;
    const id = window.setTimeout(() => setHintVisible(false), 4000);
    return () => window.clearTimeout(id);
  }, [isMobile, hintVisible]);

  useEffect(() => {
    writeState({
      ready: false,
      liveGrains: 0,
      liveFines: 0,
      pileVolume: 0,
      bursts: 0,
      fps: 0,
      frame: 0,
      budgetPressure: 0,
      sixWay: true,
      lightOrbit: false,
      tier: detectTier(),
      fineCapacity: 0,
      weapon: 'cannon',
    });
    window.__particleLabProbe = {
      burstAt(x, y, z, dx, dy, dz, mass = 1.25, nx, ny, nz) {
        worldRef.current?.burst({
          position: [x, y, z],
          direction: [dx, dy, dz],
          energy: energyRef.current,
          mass,
          profile: profileRef.current,
          surfaceNormal:
            nx !== undefined && ny !== undefined && nz !== undefined
              ? [nx, ny, nz]
              : undefined,
          impact: { kind: weaponRef.current },
        });
      },
      readFinesMeanVelocity() {
        return worldRef.current?.readFinesMeanVelocity() ?? null;
      },
      readFinesOpacity() {
        return worldRef.current?.readFinesOpacity() ?? null;
      },
      readFinesLive() {
        return worldRef.current?.readFinesLive() ?? null;
      },
      setMaterial(id) {
        setMaterial(id);
        profileRef.current = getProfile(id);
      },
      setEnergy(e) {
        const v = snapEnergy(e);
        setEnergy(v);
        energyRef.current = v;
      },
      setWeapon(kind) {
        const next: ImpactKind = IMPACT_KINDS.includes(kind) ? kind : 'cannon';
        weaponRef.current = next;
        setWeaponState(next);
        writeState({ weapon: next });
      },
      reset() {
        worldRef.current?.reset();
        writeState({
          ready: worldRef.current?.ready ?? false,
          liveGrains: 0,
          liveFines: 0,
          liveChunks: 0,
          airborneChunks: 0,
          settledChunks: 0,
          bleedEmissions: 0,
          jointPuffEmissions: 0,
          simTime: 0,
          pileVolume: 0,
          bursts: 0,
          frame: 0,
        });
      },
      setCamera(px, py, pz, tx, ty, tz) {
        const cam = controlsRef.current?.object;
        if (cam) cam.position.set(px, py, pz);
        controlsRef.current?.target.set(tx, ty, tz);
        controlsRef.current?.update();
      },
      getCamera() {
        const controls = controlsRef.current;
        const cam = controls?.object;
        if (!cam || !controls) return null;
        const tx = controls.target.x;
        const ty = controls.target.y;
        const tz = controls.target.z;
        const px = cam.position.x;
        const py = cam.position.y;
        const pz = cam.position.z;
        const dx = px - tx;
        const dy = py - ty;
        const dz = pz - tz;
        return {
          x: px,
          y: py,
          z: pz,
          tx,
          ty,
          tz,
          azimuth: Math.atan2(dx, dz),
          polar: Math.atan2(Math.hypot(dx, dz), dy),
        };
      },
      setHeightfieldView(on) {
        setShowHf(on);
        showHfRef.current = on;
      },
      sampleHeightfield(x, z) {
        return worldRef.current?.sampleHeightfieldAt(x, z) ?? null;
      },
      setDust(on) {
        setDustOn(on);
        worldRef.current?.setDust(on);
        if (!on) writeState({ liveFines: 0 });
      },
      setGrainsVisible(on) {
        worldRef.current?.setGrainsVisible(on);
      },
      setSixWay(on) {
        sixWayRef.current = on;
        setSixWayOn(on);
        worldRef.current?.setSixWay(on);
        writeState({ sixWay: on });
      },
      setLightOrbit(on) {
        lightOrbitRef.current = on;
        setLightOrbitOn(on);
        writeState({ lightOrbit: on });
      },
      setLightOrbitRate(radPerSec) {
        lightRateRef.current = Math.max(0, radPerSec);
      },
      setLightDir(x, y, z) {
        lightOrbitRef.current = false;
        setLightOrbitOn(false);
        lightPosRef.current = [x, y, z];
        worldRef.current?.setLightDir(x, y, z);
      },
      setTier(next) {
        setWorldTier(next);
        writeState({
          tier: next,
          ready: false,
          liveGrains: 0,
          liveFines: 0,
          pileVolume: 0,
          bursts: 0,
          budgetPressure: 0,
          fineCapacity: 0,
        });
      },
      burstMany(items) {
        const world = worldRef.current;
        if (!world) return;
        world.burstMany(
          items.map((it) => ({
            position: [it.x, it.y, it.z],
            direction: [it.dx, it.dy, it.dz],
            energy: it.energy ?? energyRef.current,
            mass: it.mass ?? 1.25,
            profile: it.material ? getProfile(it.material) : profileRef.current,
            surfaceNormal:
              it.nx !== undefined && it.ny !== undefined && it.nz !== undefined
                ? [it.nx, it.ny, it.nz]
                : undefined,
            impact: { kind: weaponRef.current },
          })),
        );
      },
      lastBurst() {
        return worldRef.current?.lastBurst() ?? null;
      },
      dumpCloud() {
        const sc = sceneDumpRef.current;
        const fines = sc?.getObjectByName('particle-lab-fines') as THREE.Mesh | undefined;
        const u = (fines?.material as THREE.ShaderMaterial | undefined)?.uniforms;
        if (!u) return { finesFound: Boolean(fines) };
        const tex = (slot: { value?: THREE.Texture } | undefined) => {
          const t = slot?.value;
          if (!t) return null;
          const img = t.image as { width?: number; height?: number; src?: string } | undefined;
          return {
            name: t.name || null,
            w: img?.width ?? null,
            h: img?.height ?? null,
            imagePresent: Boolean(img),
            isDummy: Boolean(t.name && t.name.includes('dummy')) || img?.width === 1,
            src: typeof img?.src === 'string' ? img.src.slice(-64) : null,
          };
        };
        const L = u.uLightDir?.value as THREE.Vector3 | undefined;
        const e = (u.uCameraMatrixWorld?.value as THREE.Matrix4 | undefined)?.elements;
        const camFront = e ? { x: +e[8].toFixed(3), y: +e[9].toFixed(3), z: +e[10].toFixed(3) } : null;
        const LdotFront =
          L && camFront ? +(L.x * camFront.x + L.y * camFront.y + L.z * camFront.z).toFixed(3) : null;
        return {
          finesFound: true,
          finesVisible: fines?.visible ?? null,
          uSixWay: u.uSixWay?.value ?? null,
          uLightDir: L ? { x: +L.x.toFixed(4), y: +L.y.toFixed(4), z: +L.z.toFixed(4) } : null,
          camFront,
          LdotFront,
          tSixA: tex(u.tSixA),
          tSixB: tex(u.tSixB),
          tSixA2: tex(u.tSixA2),
          tSixB2: tex(u.tSixB2),
        };
      },
      hideByName(name, visible) {
        const obj = sceneDumpRef.current?.getObjectByName(name);
        if (obj) obj.visible = visible;
      },
    };
    return () => {
      delete window.__particleLabProbe;
      delete window.__particleLabState;
    };
  }, []);

  const materialButtons = PARTICLE_PROFILES.map((p) => (
    <button
      key={p.id}
      type="button"
      data-testid={`material-${p.id}`}
      className={material === p.id ? 'is-on' : ''}
      aria-pressed={material === p.id}
      onClick={() => setMaterial(p.id)}
    >
      {p.label}
    </button>
  ));

  const weaponButtons = IMPACT_KINDS.map((kind) => (
    <button
      key={kind}
      type="button"
      data-testid={`weapon-${kind}`}
      className={weapon === kind ? 'is-on' : ''}
      aria-pressed={weapon === kind}
      onClick={() => {
        weaponRef.current = kind;
        setWeaponState(kind);
        writeState({ weapon: kind });
      }}
    >
      {kind}
    </button>
  ));

  const energyControl = (
    <label className="pl-row pl-energy-control">
      <span>Energy {energy.toFixed(2)}</span>
      <input
        data-testid="energy-slider"
        type="range"
        min={0}
        max={100}
        step={1}
        value={Math.round(energyToSlider(energy) * 100)}
        onChange={(e) => setEnergy(sliderToEnergy(Number(e.target.value) / 100))}
        aria-label="Burst energy"
      />
    </label>
  );

  const heightfieldToggle = (
    <button
      type="button"
      data-testid="toggle-heightfield"
      className={`pl-toggle${showHf ? ' is-on' : ''}`}
      aria-pressed={showHf}
      onClick={() => setShowHf((v) => !v)}
    >
      Heightfield
    </button>
  );

  const dustToggle = (
    <button
      type="button"
      data-testid="toggle-dust"
      className={`pl-toggle${dustOn ? ' is-on' : ''}`}
      aria-pressed={dustOn}
      onClick={() => {
        const next = !dustOn;
        setDustOn(next);
        worldRef.current?.setDust(next);
        if (!next) writeState({ liveFines: 0 });
      }}
    >
      Dust
    </button>
  );

  const sixWayToggle = (
    <button
      type="button"
      data-testid="toggle-sixway"
      className={`pl-toggle${sixWay ? ' is-on' : ''}`}
      aria-pressed={sixWay}
      onClick={() => {
        const next = !sixWay;
        sixWayRef.current = next;
        setSixWayOn(next);
        worldRef.current?.setSixWay(next);
        writeState({ sixWay: next });
      }}
    >
      6-way
    </button>
  );

  const lightOrbitToggle = (
    <button
      type="button"
      data-testid="toggle-lightorbit"
      className={`pl-toggle${lightOrbit ? ' is-on' : ''}`}
      aria-pressed={lightOrbit}
      onClick={() => {
        const next = !lightOrbit;
        lightOrbitRef.current = next;
        setLightOrbitOn(next);
        writeState({ lightOrbit: next });
      }}
    >
      Light orbit
    </button>
  );

  const tierToggle = (
    <button
      type="button"
      data-testid="toggle-tier"
      className={`pl-toggle${worldTier === 'mobile' ? ' is-on' : ''}`}
      aria-pressed={worldTier === 'mobile'}
      onClick={() => {
        const next: ParticleTier = worldTier === 'desktop' ? 'mobile' : 'desktop';
        setWorldTier(next);
        writeState({
          tier: next,
          ready: false,
          liveGrains: 0,
          liveFines: 0,
          pileVolume: 0,
          bursts: 0,
          budgetPressure: 0,
          fineCapacity: 0,
        });
      }}
    >
      {worldTier === 'mobile' ? 'Tier: mobile' : 'Tier: desktop'}
    </button>
  );

  const apiToggle = (
    <button
      type="button"
      data-testid="toggle-api"
      className={`pl-toggle${apiOpen ? ' is-on' : ''}`}
      aria-pressed={apiOpen}
      onClick={() => setApiOpen((v) => !v)}
    >
      API
    </button>
  );

  const last = hud.lastBurst;
  const apiSnippet = last
    ? `world.burst({
  position: [${last.position.map((n) => n.toFixed(2)).join(', ')}],
  direction: [${last.direction.map((n) => n.toFixed(2)).join(', ')}],
  energy: ${last.energy.toFixed(2)},
  mass: ${last.mass.toFixed(2)},
  profile: getProfile('${last.profileId}'),
  surfaceNormal: [${last.surfaceNormal.map((n) => n.toFixed(2)).join(', ')}],
  impact: { kind: '${last.kind}', intensity: ${last.intensity.toFixed(2)}, blastFraction: ${last.blastFraction.toFixed(2)} },
})`
    : `world.burst({
  position: [x, y, z],
  direction: [dx, dy, dz],
  energy,
  mass,
  profile: getProfile('brick'),
  surfaceNormal: [0, 1, 0],
})`;

  const resetButton = (
    <button
      type="button"
      data-testid="reset-lab"
      onClick={() => window.__particleLabProbe?.reset()}
    >
      Reset
    </button>
  );

  return (
    <div
      className={`particle-lab-page${isMobile ? ' is-mobile' : ''}`}
      data-testid="particle-lab-page"
    >
      {isMobile && (
        <header className="mobile-lab-appbar pl-appbar" data-testid="pl-appbar">
          <button type="button" className="pl-exit" onClick={onBack} data-testid="pl-back">
            ← Back
          </button>
          <h1 className="mobile-lab-appbar-title pl-appbar-title">PARTICLE LAB</h1>
          <button
            type="button"
            className="mobile-lab-appbar-status pl-live-stats"
            data-testid="pl-live-stats"
            aria-expanded={statsOpen}
            aria-label="Toggle full stats"
            onClick={() => setStatsOpen((v) => !v)}
          >
            {Math.round(hud.live).toLocaleString()} grains
          </button>
        </header>
      )}

      {/* Canvas lives in ONE stable stage across breakpoints — never remount on resize. */}
      <div className="pl-stage" data-testid="pl-stage">
        <Canvas
          className="pl-canvas-root"
          dpr={1}
          camera={{ position: [8.6, 4.6, 7.4], fov: 42, near: 0.1, far: 80 }}
          gl={{
            antialias: true,
            alpha: false,
            toneMapping: THREE.ACESFilmicToneMapping,
            toneMappingExposure: 1.08,
            powerPreference: 'high-performance',
          }}
          onCreated={({ gl, scene }) => {
            gl.domElement.style.touchAction = 'none';
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.08;
            scene.fog = new THREE.Fog(0x0a0b0e, 24, 54);
            scene.background = new THREE.Color(0x08090c);
          }}
        >
          <LabScene
            worldRef={worldRef}
            profileRef={profileRef}
            energyRef={energyRef}
            weaponRef={weaponRef}
            controlsRef={controlsRef}
            hfCanvasRef={hfCanvasRef}
            showHfRef={showHfRef}
            sixWayRef={sixWayRef}
            lightOrbitRef={lightOrbitRef}
            lightRateRef={lightRateRef}
            lightPosRef={lightPosRef}
            sceneDumpRef={sceneDumpRef}
            worldTier={worldTier}
            onHud={setHud}
            onBurst={dismissHint}
          />
        </Canvas>

        {isMobile && hintVisible && (
          <div className="pl-toast" data-testid="pl-toast" role="status">
            tap to burst · drag to orbit
          </div>
        )}

        {isMobile && statsOpen && (
          <div className="pl-stats-overlay" data-testid="particle-hud">
            <div>
              grains <b>{Math.round(hud.live).toLocaleString()}</b>
            </div>
            <div>
              fines <b>{Math.round(hud.fines).toLocaleString()}</b>
            </div>
            <div>
              fps <b>{hud.fps.toFixed(0)}</b>
            </div>
            <div>
              pile <b>{hud.vol.toFixed(3)}</b> m³
            </div>
            <div>bursts {hud.bursts}</div>
            <div data-testid="hud-weapon">
              weapon <b>{weapon}</b>
            </div>
          </div>
        )}

        {!isMobile && (
          <div className="pl-overlay">
            <div className="pl-brand">
              <button className="pl-exit" type="button" onClick={onBack}>
                ← Lab
              </button>
              <h1 className="pl-title">Particle Lab</h1>
              <p className="pl-sub">Phase 3 — graded six-way smoke</p>
            </div>

            <aside className="pl-panel">
              <h2>Material</h2>
              <div className="pl-mats">{materialButtons}</div>
              <h2>Weapon</h2>
              <div className="pl-mats pl-weapons">{weaponButtons}</div>
              {energyControl}
              <div className="pl-actions">
                {resetButton}
                {heightfieldToggle}
                {dustToggle}
              </div>
              <div className="pl-actions">
                {sixWayToggle}
                {lightOrbitToggle}
              </div>
              <div className="pl-actions">
                {tierToggle}
                {apiToggle}
              </div>
            </aside>

            {apiOpen && (
              <pre className="pl-api" data-testid="api-panel">
                {apiSnippet}
                {last ? `\n// allocated ${last.grains} grains / ${last.fines} fines` : ''}
              </pre>
            )}

            <div className="pl-hud" data-testid="particle-hud">
              <div>
                grains <b>{Math.round(hud.live).toLocaleString()}</b>
              </div>
              <div>
                fines <b>{Math.round(hud.fines).toLocaleString()}</b>
                {hud.fineCapacity > 0 ? ` / ${hud.fineCapacity}` : ''}
              </div>
              <div>
                fps <b>{hud.fps.toFixed(0)}</b>
              </div>
              <div>
                pile <b>{hud.vol.toFixed(3)}</b> m³
              </div>
              <div>bursts {hud.bursts}</div>
              <div>
                budget <b>{hud.budgetPressure.toFixed(2)}</b>
              </div>
              <div>tier {worldTier}</div>
              <div data-testid="hud-weapon">
                weapon <b>{weapon}</b>
              </div>
            </div>

            <div className="pl-hint">click to burst · drag to orbit</div>

            {showHf && (
              <div className="pl-hf">
                <canvas ref={hfCanvasRef} width={160} height={160} />
              </div>
            )}
          </div>
        )}

        {hud.frame > 2 && !hud.ready && (
          <div className="pl-warn">
            Float render targets are unavailable in this WebGL context. The grain sim cannot start.
          </div>
        )}
      </div>

      {isMobile && (
        <div className="mobile-lab-dock pl-dock" data-testid="pl-dock">
          <div className="pl-dock-materials" data-testid="material-selector">
            {materialButtons}
          </div>
          <div className="pl-dock-materials pl-dock-weapons" data-testid="weapon-selector">
            {weaponButtons}
          </div>
          <div className="pl-dock-controls">
            {energyControl}
            {resetButton}
            {heightfieldToggle}
            {dustToggle}
          </div>
          {showHf && (
            <div className="pl-hf pl-hf-dock" data-testid="pl-hf-inset">
              <canvas ref={hfCanvasRef} width={120} height={120} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
